import { useEffect, useState } from "react";
import { X, Palette, FileText, Rocket, Link2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import DesignTab from "./tabs/DesignTab";
import ContentTab from "./tabs/ContentTab";
import DeploymentTab from "./tabs/DeploymentTab";

type SiteInfo = {
  id: string;
  name: string;
  domain: string | null;
};

type Props = {
  site: SiteInfo;
  onClose: () => void;
};

export default function SiteManagementPanel({ site, onClose }: Props) {
  const [imported, setImported] = useState(false);
  const [originalTheme, setOriginalTheme] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("websites")
      .select("technical_config")
      .eq("id", site.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const tc = (data?.technical_config as any) || {};
        setImported(!!tc.imported);
        setOriginalTheme(tc.original_theme || null);
      });
    return () => { cancelled = true; };
  }, [site.id]);

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-card/50">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">{site.name}</h2>
            {imported && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 text-[9px] font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                <Link2 className="h-2.5 w-2.5" /> Imported
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground">{site.domain || "No domain"}</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 hover:bg-accent transition-colors"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="content" className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent px-4 h-auto py-1 gap-1">
          <TabsTrigger value="design" className="text-xs gap-1.5 data-[state=active]:bg-accent rounded-md px-3 py-1.5">
            <Palette className="h-3.5 w-3.5" /> Design
          </TabsTrigger>
          <TabsTrigger value="content" className="text-xs gap-1.5 data-[state=active]:bg-accent rounded-md px-3 py-1.5">
            <FileText className="h-3.5 w-3.5" /> Content
          </TabsTrigger>
          <TabsTrigger value="deploy" className="text-xs gap-1.5 data-[state=active]:bg-accent rounded-md px-3 py-1.5">
            <Rocket className="h-3.5 w-3.5" /> Deploy
          </TabsTrigger>
        </TabsList>

        <ScrollArea className="flex-1">
          <div className="mx-auto max-w-[1400px] p-6">
            <TabsContent value="design" className="mt-0">
              <DesignTab siteId={site.id} domain={site.domain} imported={imported} originalTheme={originalTheme} />
            </TabsContent>
            <TabsContent value="content" className="mt-0">
              <ContentTab siteId={site.id} siteName={site.name} domain={site.domain} />
            </TabsContent>
            <TabsContent value="deploy" className="mt-0">
              <DeploymentTab siteId={site.id} domain={site.domain} imported={imported} />
            </TabsContent>
          </div>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
